import { Builder } from '@builder.io/react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useMutation from '../../../../../hooks/useMutaion';
import services from '../../../../../services';

// Helper to render field by type
const renderField = (field, register, errors) => {
  switch (field.type) {
    case 'text':
    case 'email':
    case 'number':
      return (
        <div key={field.name}>
          <input
            {...register(field.name, field.validation || {})}
            placeholder={field.placeholder || field.label}
            type={field.type}
            className="w-full p-2 border rounded-lg text-base border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-transparent"
          />
          {errors[field.name]?.message && (
            <span className="text-sm text-red-600">{errors[field.name].message}</span>
          )}
        </div>
      );
    case 'select':
      return (
        <div key={field.name}>
          <select
            defaultValue={''}
            className="w-full p-2 border rounded-lg text-base border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-transparent"
            {...register(field.name, field.validation || {})}
          >
            <option value="">{field.placeholder || '--Please choose an option--'}</option>
            {(field.options || []).map((opt, idx) => (
              <option value={opt.value} key={idx}>{opt.label}</option>
            ))}
          </select>
          {errors[field.name]?.message && (
            <span className="text-sm text-red-600">{errors[field.name].message}</span>
          )}
        </div>
      );
    case 'textarea':
      return (
        <div key={field.name} className={field.className || ''}>
          <textarea
            {...register(field.name, field.validation || {})}
            placeholder={field.placeholder || field.label}
            className="w-full p-2 border rounded-lg text-base border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-transparent"
          />
          {errors[field.name]?.message && (
            <span className="text-sm text-red-600">{errors[field.name].message}</span>
          )}
        </div>
      );
    default:
      return null;
  }
};

const ContactForm = ({ fields = defaultFields, submitButtonText = 'Send Message', containerClassNames = '' }) => {
  const { error, isLoading, mutate, isSuccess } = useMutation(services.postFormContact);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onSubmit' });

  const onSubmit = async data => {
    const ip = await services.getIp();
    const agent = navigator.userAgent;
    data.agent = agent;
    data.ip = ip.data;
    mutate(data);
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success('Thank you for contacting us. We will get back to you soon.');
      reset();
    }
    if (error) {
      toast.error('Something went wrong. Please try again later.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, error]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={`w-full text-sm text-textPrimary gap-4 grid grid-cols-1 md:grid-cols-2 ${containerClassNames}`}>
      {fields.map(field => renderField(field, register, errors))}
      <div className="md:col-span-2 col-span-1 flex justify-center items-center">
        <button type="submit" disabled={isLoading} className="rounded-xl bg-[#282828] text-white hover:opacity-7 transition-all duration-300 px-16 py-3">
          {isLoading ? 'Loading...' : submitButtonText}
        </button>
      </div>
    </form>
  );
};

// Default fields for backward compatibility
const defaultFields = [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    placeholder: 'Name',
    validation: { required: 'This field is required' },
  },
  {
    name: 'email',
    label: 'E-mail',
    type: 'email',
    placeholder: 'E-mail',
    validation: {
      required: 'This field is required',
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'invalid email address',
      },
    },
  },
  {
    name: 'phone',
    label: 'Phone',
    type: 'number',
    placeholder: 'Phone',
    validation: { required: 'This field is required' },
  },
  {
    name: 'purpose',
    label: 'Purpose',
    type: 'select',
    placeholder: '--Please choose an option--',
    options: [
      { value: 'Complaint', label: 'Complaint' },
      { value: 'donationQuestion', label: 'Donation Question' },
      { value: 'progressQuery', label: 'Progress Query' },
      { value: 'Other', label: 'General enquiry' },
    ],
    validation: { required: 'This field is required' },
  },
  {
    name: 'message',
    label: 'Message',
    type: 'textarea',
    placeholder: 'Message',
    className: 'md:col-span-2 col-span-1',
    validation: { required: 'This field is required' },
  },
];

Builder.registerComponent(ContactForm, {
  name: 'ContactForm',
  inputs: [
    {
      name: 'fields',
      type: 'list',
      subFields: [
        { name: 'name', type: 'string', required: true },
        { name: 'label', type: 'string' },
        { name: 'type', type: 'string', required: true, enum: ['text', 'email', 'number', 'select', 'textarea'] },
        { name: 'placeholder', type: 'string' },
        { name: 'className', type: 'string' },
        { name: 'options', type: 'list', subFields: [ { name: 'value', type: 'string' }, { name: 'label', type: 'string' } ] },
        // Validation is not directly editable in Builder UI, but can be extended in code
      ],
      helperText: 'Configure the fields for the contact form',
      defaultValue: defaultFields,
      friendlyName: 'Form Fields',
    },
    {
      name: 'submitButtonText',
      type: 'string',
      defaultValue: 'Send Message',
      friendlyName: 'Submit Button Text',
    },
    {
      name: 'containerClassNames',
      type: 'string',
      defaultValue: '',
      friendlyName: 'Main Container Tailwind Classes',
    },
  ],
});

export default ContactForm;